import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { clearStoredSession } from '../lib/apiAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DomioLogo from '../components/common/DomioLogo';
import { Lock, AlertCircle, Loader2, Check, ArrowLeft } from 'lucide-react';
import { useI18n } from '../i18n';
import { toast } from 'sonner';

export const ResetPasswordPage = () => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    clearStoredSession();
  }, []);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      try {
        const res = await authAPI.verifyResetToken(token);
        setValid(true);
        setEmail(res.data.email || '');
      } catch {
        setError(t('auth.invalidResetLink'));
      }
      setVerifying(false);
    };
    verify();
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authAPI.resetPassword({ token, new_password: password });
      toast.success(t('auth.passwordResetSuccess'));
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.passwordResetFailed'));
    }
    setLoading(false);
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-domio-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center gap-2 mb-6">
          <DomioLogo size="lg" showText textClassName="text-slate-900" />
        </div>

        <Link to="/login" className="inline-flex items-center text-sm text-domio-600 hover:text-domio-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('auth.backToLogin')}
        </Link>

        {!valid ? (
          <div className="text-center py-6">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-manrope font-bold text-xl text-slate-900 mb-2">{t('auth.invalidResetLink')}</h2>
            <p className="text-slate-500 text-sm mb-4">{error}</p>
            <Link to="/forgot-password" className="text-domio-600 hover:underline text-sm font-medium">
              {t('auth.requestNewResetLink')}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-manrope text-2xl font-bold text-slate-900 mb-2">{t('auth.resetPasswordTitle')}</h1>
            <p className="text-slate-500 text-sm mb-6">{t('auth.resetPasswordSubtitle')}</p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <Input value={email} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    placeholder={t('auth.enterPassword')}
                    required
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-domio-600 hover:bg-domio-700 text-white"
                data-testid="reset-password-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                {t('auth.resetPasswordButton')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;

